import React from 'react';
import { CategoryDefinition } from '../../config/categoryConfig';
import StartCategoryButton from './StartCategoryButton';
import { cn } from '../../utils/cn';

interface StartCategoryGridProps {
  categories: CategoryDefinition[];
  selectedCategory: string | null;
  onCategoryTap: (category: CategoryDefinition) => void;
  onContinue: (category: CategoryDefinition) => void;
  isCategoryLocked: (slug: string) => boolean;
}

const StartCategoryGrid = React.memo(function StartCategoryGrid({
  categories,
  selectedCategory,
  onCategoryTap,
  onContinue,
  isCategoryLocked,
}: StartCategoryGridProps) {
  const selectedCategoryData = categories.find((cat) => cat.slug === selectedCategory);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-4 py-8">
      {/* Logo/Title */}
      <div className="text-center mb-8 bg-background rounded-2xl p-6 shadow-lg">
        <h1 className="text-4xl font-bold mb-2 text-foreground">
          Wallstreetle
        </h1>
        <p className="text-lg text-foreground">
          Pick a stock category to play
        </p>
      </div>

      <div className="w-full max-w-md space-y-4">
        {/* All 13 categories: 5x3 grid (with 2 empty slots) */}
        <div className="grid grid-cols-3 gap-4">
          {categories.map((category, index) => {
            const isLocked = isCategoryLocked(category.slug);
            const isSelected = selectedCategory === category.slug;

            return (
              <StartCategoryButton
                key={category.slug}
                category={category}
                isLocked={isLocked}
                isSelected={isSelected}
                onClick={() => onCategoryTap(category)}
                variant="grid"
                className={cn(
                  'animate-bounce-in',
                  'touch-lg' // 48px touch target
                )}
                style={{
                  animationDelay: `${index * 50}ms`, // Staggered animation
                }}
              />
            );
          })}
        </div>
      </div>

      {/* Description panel (shown when category selected) */}
      {selectedCategoryData && (
        <div
          className={cn(
            'fixed bottom-0 left-0 right-0',
            'bg-card dark:bg-gray-800',
            'border-t-2 dark:border-gray-700',
            'p-6 shadow-lg',
            'animate-slide-up'
          )}
          style={{
            borderTopColor: selectedCategoryData.color,
          }}
        >
          <div className="max-w-md mx-auto">
            <div className="flex items-center gap-3 mb-3">
              <span
                className="text-4xl"
                role="img"
                aria-label={selectedCategoryData.displayName}
              >
                {selectedCategoryData.icon}
              </span>
              <h3 className="text-xl font-bold text-foreground">
                {selectedCategoryData.displayName}
              </h3>
            </div>
            <p className="text-foreground mb-4">
              {selectedCategoryData.seoDescription}
            </p>
            <button
              onClick={() => onContinue(selectedCategoryData)}
              className={cn(
                'w-full py-3 px-6 rounded-lg',
                'font-semibold text-white',
                'transition-all duration-200',
                'hover:scale-105 active:scale-95',
                'focus:outline-none focus:ring-2 focus:ring-offset-2',
                'min-h-touch-lg' // 48px touch target
              )}
              style={{
                backgroundColor: selectedCategoryData.color,
              }}
            >
              Continue →
            </button>
          </div>
        </div>
      )}

      {/* Accessibility: Reduced motion support is handled via global CSS */}
    </div>
  );
});

export default StartCategoryGrid;
